import React, { useState, useRef, useEffect } from 'react';
import {
  Upload, FileText, Zap, X, CircleCheck, CircleX, MinusCircle,
  Loader2, ArrowRight, BookOpen, Info,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { getFrameworks, runCheck } from '../../utils/api';

export default function RunCheck() {
  const [fileObjs, setFileObjs] = useState([]); // actual File objects
  const [fileDisplay, setFileDisplay] = useState([]); // display info
  const [framework, setFramework] = useState('CSRD');
  const [frameworks, setFrameworks] = useState([]);
  const [scopes, setScopes] = useState({ s1: true, s2: true, s3: false });
  const [entityName, setEntityName] = useState('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    getFrameworks().then(setFrameworks).catch(() => {});
  }, []);

  const onPickFiles = (list) => {
    const arr = Array.from(list || []);
    setFileObjs((prev) => [...prev, ...arr]);
    setFileDisplay((prev) => [...prev, ...arr.map((f) => ({
      name: f.name,
      size: f.size,
      type: f.name.split('.').pop()?.toUpperCase() || 'FILE',
    }))]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    onPickFiles(e.dataTransfer.files);
  };

  const removeFile = (i) => {
    setFileObjs((prev) => prev.filter((_, idx) => idx !== i));
    setFileDisplay((prev) => prev.filter((_, idx) => idx !== i));
  };

  const runEvaluation = async () => {
    if (fileObjs.length === 0) {
      toast.error('Upload at least one document to run a check.');
      return;
    }
    setRunning(true);
    setProgress(0);
    setResult(null);

    // Animate progress while waiting for API
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 90) { clearInterval(interval); return 90; }
        return p + 2;
      });
    }, 300);

    try {
      const name = entityName || fileObjs[0].name.replace(/\.[^/.]+$/, '');
      const data = await runCheck(
        fileObjs[0],
        framework,
        name,
        'medium',
        scopes
      );

      clearInterval(interval);
      setProgress(100);

      // Format result from API response
      const results = data.results_json || [];
      const met = results.filter(r => r.outcome === 'pass').length;
      const notMet = results.filter(r => r.outcome === 'fail').length;
      const na = results.filter(r => r.outcome === 'not_applicable').length;
      const total = results.length;
      const score = data.overall_score ? parseFloat(data.overall_score) : Math.round((met / Math.max(total - na, 1)) * 100);

      setResult({
        framework,
        score: score.toFixed(0),
        met,
        notMet,
        na,
        total,
        outcome: data.compliant ? 'Compliant' : 'Non-Compliant',
        risk: data.risk_level || (score >= 85 ? 'LOW' : 'MEDIUM'),
        results,
        checkId: data.check_id,
      });
      toast.success('Compliance evaluation complete.');
    } catch (err) {
      clearInterval(interval);
      setProgress(0);
      toast.error(err.message || 'Check failed. Please try again.');
    } finally {
      setRunning(false);
    }
  };

  const exts = ['PDF', 'DOCX', 'JSON', 'TXT'];

  return (
    <div className="max-w-[1280px]">
      <div className="mb-8">
        <div className="text-[11.5px] tracking-[0.16em] uppercase text-[#1B6F5E] font-medium">Run Check</div>
        <h2 className="mt-2 font-display text-[36px] lg:text-[44px] leading-[1.05] text-[#0B1F2D]">
          Compliance <span className="italic">check.</span>
        </h2>
        <p className="mt-3 text-[14.5px] leading-[1.55] text-[#0B1F2D]/65 max-w-[700px]">
          Upload your ESG document, select a framework, and run the ESG-as-Code® engine. Every outcome maps to a
          regulatory clause. Every gap comes with remediation guidance.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: upload */}
        <div className="lg:col-span-7 space-y-5">
          {/* Upload card */}
          <div className="rounded-2xl border border-[#E8E5DC] bg-white p-7">
            <div className="flex items-center gap-2 text-[11.5px] tracking-[0.12em] uppercase text-[#1B6F5E] font-medium">
              <Zap className="w-3.5 h-3.5" />
              <span>Step 1 · Document</span>
            </div>
            <div className="mt-2 font-display text-[24px] text-[#0B1F2D]">Upload ESG document</div>
            <p className="mt-2 text-[13.5px] leading-[1.55] text-[#0B1F2D]/65 max-w-[560px]">
              Upload your sustainability report, climate disclosure, or ESG submission. PDF, DOCX, JSON, and TXT supported.
            </p>

            {/* Entity name */}
            <div className="mt-5">
              <label className="text-[11.5px] uppercase tracking-[0.1em] text-[#0B1F2D]/60 block mb-2">
                Entity / company name (optional)
              </label>
              <input
                type="text"
                value={entityName}
                onChange={(e) => setEntityName(e.target.value)}
                placeholder="e.g. Acme Corp"
                className="w-full h-11 px-3 rounded-lg border border-[#E8E5DC] bg-[#FAFAF5] text-[14px] text-[#0B1F2D] focus:outline-none focus:ring-2 focus:ring-[#1B6F5E]"
              />
            </div>

            <div
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => inputRef.current?.click()}
              className="mt-5 rounded-2xl border-2 border-dashed border-[#0B1F2D]/20 bg-[#FAFAF5] hover:border-[#1B6F5E] hover:bg-[#E8F3EE]/40 transition-colors cursor-pointer p-8 text-center"
            >
              <div className="w-12 h-12 mx-auto rounded-xl bg-[#E8F3EE] text-[#1B6F5E] flex items-center justify-center">
                <Upload className="w-5 h-5" />
              </div>
              <div className="mt-4 font-display text-[18px] text-[#0B1F2D]">Drop your document here</div>
              <div className="text-[12.5px] text-[#0B1F2D]/55">or click to browse files</div>
              <div className="mt-4 flex items-center justify-center gap-2">
                {exts.map((e) => (
                  <span key={e} className="font-mono-tight text-[10.5px] px-2 py-1 rounded bg-white border border-[#E8E5DC] text-[#0B1F2D]/65">
                    {e}
                  </span>
                ))}
              </div>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.json,.txt"
                onChange={(e) => onPickFiles(e.target.files)}
                className="hidden"
              />
            </div>

            {fileDisplay.length > 0 && (
              <div className="mt-5 space-y-2">
                {fileDisplay.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-[#E8E5DC] bg-[#FAFAF5]/50">
                    <div className="w-8 h-8 rounded-md bg-white border border-[#E8E5DC] flex items-center justify-center text-[#0B1F2D]/70">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] text-[#0B1F2D] font-medium truncate">{f.name}</div>
                      <div className="text-[11px] text-[#0B1F2D]/55 font-mono-tight">
                        {f.type} · {(f.size / 1024).toFixed(0)} KB
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                      className="w-7 h-7 rounded-md hover:bg-white flex items-center justify-center text-[#0B1F2D]/60 hover:text-[#A4243B]"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Framework + scope */}
          <div className="rounded-2xl border border-[#E8E5DC] bg-white p-7">
            <div className="flex items-center gap-2 text-[11.5px] tracking-[0.12em] uppercase text-[#1B6F5E] font-medium">
              <BookOpen className="w-3.5 h-3.5" />
              <span>Step 2 · Framework &amp; scopes</span>
            </div>
            <div className="mt-2 font-display text-[24px] text-[#0B1F2D]">Select framework</div>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {frameworks.map((fw) => (
                <button
                  key={fw.code}
                  onClick={() => setFramework(fw.code)}
                  className={`px-3 py-3 rounded-xl border text-left transition-colors ${
                    framework === fw.code
                      ? 'border-[#0B1F2D] bg-[#0B1F2D] text-[#FAFAF5]'
                      : 'border-[#E8E5DC] bg-white hover:border-[#0B1F2D]/40'
                  }`}
                >
                  <div className="font-mono-tight text-[10px] opacity-70">{fw.jurisdiction}</div>
                  <div className="text-[13px] font-medium mt-0.5">{fw.name}</div>
                  <div className="text-[10.5px] opacity-60 mt-1">{fw.activeRules} rules</div>
                </button>
              ))}
            </div>

            <div className="mt-7">
              <div className="text-[12px] uppercase tracking-[0.1em] text-[#0B1F2D]/60">GHG Scopes applicability</div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {[
                  { k: 's1', label: 'Scope 1', desc: 'Direct' },
                  { k: 's2', label: 'Scope 2', desc: 'Indirect, energy' },
                  { k: 's3', label: 'Scope 3', desc: 'Value chain' },
                ].map((sc) => (
                  <label
                    key={sc.k}
                    className={`cursor-pointer rounded-xl border px-4 py-3 transition-colors ${
                      scopes[sc.k]
                        ? 'border-[#1B6F5E] bg-[#E8F3EE]/60'
                        : 'border-[#E8E5DC] bg-white hover:border-[#0B1F2D]/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[13px] font-medium text-[#0B1F2D]">{sc.label}</div>
                      <input
                        type="checkbox"
                        checked={scopes[sc.k]}
                        onChange={(e) => setScopes({ ...scopes, [sc.k]: e.target.checked })}
                        className="accent-[#1B6F5E]"
                      />
                    </div>
                    <div className="text-[11px] text-[#0B1F2D]/55 mt-1">{sc.desc}</div>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-7 flex items-center justify-end">
              <Button
                onClick={runEvaluation}
                disabled={running}
                className="bg-[#0B1F2D] hover:bg-[#0B1F2D]/90 text-[#FAFAF5] rounded-full h-12 px-6 text-[13.5px] inline-flex items-center gap-2 disabled:opacity-70"
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {running ? 'Evaluating...' : 'Run compliance check'}
              </Button>
            </div>
          </div>

          {/* Guidelines */}
          <div className="rounded-2xl border border-[#E8E5DC] bg-[#E8F3EE]/40 p-6">
            <div className="flex items-center gap-2 text-[12px] font-medium text-[#1B6F5E]">
              <Info className="w-3.5 h-3.5" />
              Upload guidelines
            </div>
            <ul className="mt-3 space-y-2 text-[12.5px] text-[#0B1F2D]/75 leading-[1.55]">
              <li>Maximum file size: <span className="font-medium text-[#0B1F2D]">25 MB per document</span>.</li>
              <li>For best AI extraction accuracy, keep each document under <span className="font-medium text-[#0B1F2D]">50 pages</span>.</li>
              <li>If your ESG report exceeds 50 pages, split it into sections and upload each separately.</li>
              <li>Upload supporting documents individually: ESG Data Book, TCFD Report, CDP Submission.</li>
              <li>Evidence from multiple uploads is combined before the rules engine evaluates.</li>
            </ul>
          </div>
        </div>

        {/* Right: Results */}
        <div className="lg:col-span-5">
          <div className="sticky top-[88px] rounded-2xl border border-[#E8E5DC] bg-white overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E8E5DC] bg-[#FAFAF5]/60 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11.5px] tracking-[0.12em] uppercase text-[#1B6F5E] font-medium">
                <CircleCheck className="w-3.5 h-3.5" />
                ESG-as-Code® engine output
              </div>
              {result && (
                <span className="font-mono-tight text-[10.5px] text-[#0B1F2D]/55">/{result.framework}</span>
              )}
            </div>

            <div className="p-7">
              {!running && !result && (
                <div className="py-10 text-center">
                  <div className="w-14 h-14 mx-auto rounded-xl bg-[#FAFAF5] border border-[#E8E5DC] flex items-center justify-center text-[#0B1F2D]/45">
                    <Zap className="w-6 h-6" />
                  </div>
                  <div className="mt-5 font-display text-[20px] text-[#0B1F2D]">Upload a document to begin</div>
                  <p className="mt-2 text-[12.5px] text-[#0B1F2D]/55 max-w-[320px] mx-auto leading-[1.55]">
                    Upload your ESG report on the left, select a framework, and run the engine.
                  </p>
                </div>
              )}

              {running && (
                <div className="py-10">
                  <div className="flex items-center justify-between text-[12px] text-[#0B1F2D]/70">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1B6F5E]" />
                      Evaluating {framework} rules
                    </span>
                    <span className="font-mono-tight">{progress}%</span>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-[#E8E5DC] overflow-hidden">
                    <div
                      className="h-full bg-[#1B6F5E] transition-[width] duration-300 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <ul className="mt-6 space-y-2 text-[12.5px] text-[#0B1F2D]/65">
                    {[
                      'Extracting structured fields',
                      'Building evidence pool',
                      'Running deterministic rule set',
                      'Mapping outcomes to regulatory clauses',
                    ].map((t, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${progress > (i + 1) * 20 ? 'bg-[#1B6F5E]' : 'bg-[#0B1F2D]/15'}`} />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result && !running && (
                <div>
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <div className="text-[11.5px] uppercase tracking-[0.12em] text-[#0B1F2D]/55">Result</div>
                      <div className="mt-1 font-display text-[44px] leading-none text-[#0B1F2D]">
                        {result.score}%
                      </div>
                    </div>
                    <span className={`text-[11px] font-medium tracking-[0.08em] uppercase px-3 py-1.5 rounded-full border ${
                      result.outcome === 'Compliant'
                        ? 'bg-[#E8F3EE] text-[#1B6F5E] border-[#1B6F5E]/25'
                        : 'bg-[#FBEAEA] text-[#A4243B] border-[#A4243B]/25'
                    }`}>
                      {result.outcome}
                    </span>
                  </div>

                  <div className="mt-6 grid grid-cols-3 gap-2">
                    <div className="rounded-xl border border-[#E8E5DC] bg-[#FAFAF5] p-3 text-center">
                      <CircleCheck className="w-4 h-4 mx-auto text-[#1B6F5E]" />
                      <div className="mt-1 font-display text-[20px] text-[#0B1F2D]">{result.met}</div>
                      <div className="text-[10.5px] uppercase tracking-[0.1em] text-[#0B1F2D]/55">Met</div>
                    </div>
                    <div className="rounded-xl border border-[#E8E5DC] bg-[#FAFAF5] p-3 text-center">
                      <CircleX className="w-4 h-4 mx-auto text-[#A4243B]" />
                      <div className="mt-1 font-display text-[20px] text-[#0B1F2D]">{result.notMet}</div>
                      <div className="text-[10.5px] uppercase tracking-[0.1em] text-[#0B1F2D]/55">Not Met</div>
                    </div>
                    <div className="rounded-xl border border-[#E8E5DC] bg-[#FAFAF5] p-3 text-center">
                      <MinusCircle className="w-4 h-4 mx-auto text-[#0B1F2D]/55" />
                      <div className="mt-1 font-display text-[20px] text-[#0B1F2D]">{result.na}</div>
                      <div className="text-[10.5px] uppercase tracking-[0.1em] text-[#0B1F2D]/55">N/A</div>
                    </div>
                  </div>

                  {/* Rule results */}
                  {result.results && result.results.length > 0 && (
                    <div className="mt-6 space-y-2 max-h-[300px] overflow-y-auto">
                      <div className="text-[11px] uppercase tracking-[0.1em] text-[#0B1F2D]/55 mb-3">Rule breakdown</div>
                      {result.results.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 p-2 rounded-lg border border-[#E8E5DC] bg-[#FAFAF5]/50">
                          {r.outcome === 'pass' && <CircleCheck className="w-4 h-4 text-[#1B6F5E] mt-0.5 shrink-0" />}
                          {r.outcome === 'fail' && <CircleX className="w-4 h-4 text-[#A4243B] mt-0.5 shrink-0" />}
                          {r.outcome === 'not_applicable' && <MinusCircle className="w-4 h-4 text-[#0B1F2D]/40 mt-0.5 shrink-0" />}
                          <div className="min-w-0">
                            <div className="text-[11.5px] font-medium text-[#0B1F2D]">{r.ruleCode || r.rule}</div>
                            <div className="text-[11px] text-[#0B1F2D]/60 mt-0.5">{r.reasoning || r.reason}</div>
                            {r.recommendation && (
                              <div className="text-[10.5px] text-[#A4243B] mt-1 leading-[1.4]">{r.recommendation}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 pt-5 border-t border-[#E8E5DC] flex items-center justify-between text-[12px]">
                    <div>
                      <div className="text-[#0B1F2D]/55 uppercase tracking-[0.1em] text-[10.5px]">Risk</div>
                      <div className="mt-0.5 font-mono-tight text-[#0B1F2D] font-semibold">{result.risk}</div>
                    </div>
                    <div>
                      <div className="text-[#0B1F2D]/55 uppercase tracking-[0.1em] text-[10.5px]">Rules</div>
                      <div className="mt-0.5 font-mono-tight text-[#0B1F2D] font-semibold">{result.total} total</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
